import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Cloud, Globe, Rocket } from 'lucide-react';
import { navLinks } from '../../data/mock';

export const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);

  const products = [
    {
      name: 'Parler Cloud',
      description: 'High-performance cloud infrastructure with predictable packages...',
      path: '/cloud',
      icon: Cloud,
      badge: 'beta'
    },
    {
      name: 'Parler CDN',
      description: 'Deliver globally with sub-50ms edge latency and enterprise-grade...',
      path: '/cdn',
      icon: Globe,
      badge: 'beta'
    },
    {
      name: 'Parler Deploy',
      description: 'Ship modern web apps with Git workflows, previews, instant...',
      path: '/deploy',
      icon: Rocket,
      badge: 'beta'
    }
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-white/10">
        <div className="flex items-center justify-between h-16 md:h-20 px-4 md:px-8 lg:px-12">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src="/parler-logo.svg" 
              alt="Parler Cloud" 
              className="h-7 md:h-9 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 lg:gap-2">
            <Link
              to="/"
              className={`px-3 lg:px-4 py-2 text-sm lg:text-base font-medium transition-colors ${
                location.pathname === '/' 
                  ? 'text-[#2a5ff6]' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Home
            </Link>

            {/* Products Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <button
                className={`flex items-center gap-1 px-3 lg:px-4 py-2 text-sm lg:text-base font-medium transition-colors ${
                  ['/cloud', '/cdn', '/deploy'].includes(location.pathname)
                    ? 'text-[#2a5ff6]' 
                    : 'text-white/60 hover:text-white'
                }`}
              >
                Products
                <ChevronDown size={16} className={`transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              <div 
                className={`absolute top-full left-0 pt-2 w-80 transition-all duration-200 ${
                  productsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                <div className="bg-[#0d1526] border border-white/10 shadow-2xl rounded-xl overflow-hidden">
                  {products.map((product) => {
                    const Icon = product.icon;
                    return (
                      <Link
                        key={product.path}
                        to={product.path}
                        className="flex items-start gap-4 p-4 hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0"
                        onClick={() => setProductsOpen(false)}
                      >
                        <Icon size={24} className="text-[#2a5ff6] mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-white font-semibold mb-1 flex items-center gap-2">
                            {product.name}
                            {product.badge && (
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-[#2a5ff6]/20 text-[#2a5ff6] px-1.5 py-0.5 rounded">
                                {product.badge}
                              </span>
                            )}
                          </div>
                          <div className="text-white/50 text-sm leading-relaxed">{product.description}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <Link
              to="/packages"
              className={`px-3 lg:px-4 py-2 text-sm lg:text-base font-medium transition-colors ${
                location.pathname === '/packages' 
                  ? 'text-[#2a5ff6]' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Pricing
            </Link>

            <Link
              to="/comparison"
              className={`px-3 lg:px-4 py-2 text-sm lg:text-base font-medium transition-colors ${
                location.pathname === '/comparison' 
                  ? 'text-[#2a5ff6]' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Comparison
            </Link>

            <Link
              to="/contact"
              className={`px-3 lg:px-4 py-2 text-sm lg:text-base font-medium transition-colors ${
                location.pathname === '/contact' 
                  ? 'text-[#2a5ff6]' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* CTA Button - Desktop */}
          <Link
            to="/contact"
            className="hidden md:inline-flex items-center gap-2 bg-[#2a5ff6] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#4d7ff8] transition-colors rounded-xl"
          >
            Get Started
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-40 bg-black"
          style={{ paddingTop: '64px' }}
        >
          <div className="flex flex-col p-6 h-full overflow-auto">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`py-4 text-xl font-medium border-b border-white/10 ${
                location.pathname === '/' ? 'text-[#2a5ff6]' : 'text-white'
              }`}
            >
              Home
            </Link>

            {/* Products Section */}
            <div className="py-4 border-b border-white/10">
              <div className="text-white/50 text-sm uppercase tracking-wider mb-3">Products</div>
              {products.map((product) => {
                const Icon = product.icon;
                return (
                  <Link
                    key={product.path}
                    to={product.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 py-3 ${
                      location.pathname === product.path ? 'text-[#2a5ff6]' : 'text-white'
                    }`}
                  >
                    <Icon size={20} />
                    {product.name}
                    {product.badge && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-[#2a5ff6]/20 text-[#2a5ff6] px-1.5 py-0.5 rounded">
                        {product.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>

            <Link
              to="/packages"
              onClick={() => setMobileMenuOpen(false)}
              className={`py-4 text-xl font-medium border-b border-white/10 ${
                location.pathname === '/packages' ? 'text-[#2a5ff6]' : 'text-white'
              }`}
            >
              Pricing
            </Link>

            <Link
              to="/comparison"
              onClick={() => setMobileMenuOpen(false)}
              className={`py-4 text-xl font-medium border-b border-white/10 ${
                location.pathname === '/comparison' ? 'text-[#2a5ff6]' : 'text-white'
              }`}
            >
              Comparison
            </Link>

            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className={`py-4 text-xl font-medium border-b border-white/10 ${
                location.pathname === '/contact' ? 'text-[#2a5ff6]' : 'text-white'
              }`}
            >
              Contact
            </Link>

            <Link
              to="/packages"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-6 bg-[#2a5ff6] text-white py-4 text-center font-semibold text-lg rounded-xl"
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </>
  );
};
